import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, Globe } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { PERMISSIONS } from '@/lib/permissions'
import { EmptyState, ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'
import type { CountryRow } from '@/lib/adapters'
import { fetchAllCountries, GEO_STR as S } from './geoApi'

/**
 * A11.01 — Davlatlar xaritasi. REAL `/admin/countries`: dunyo qamrovi obzori —
 * ochiq/bloklangan davlatlar plitkalari (users bo'yicha) + KPI. Alohida endpoint
 * yo'q; blok/karta amali `/geo` (A11.02) + `/geo/:code` (A11.03) ekranlarida.
 */
export function CountriesMapScreen() {
  const { can } = useAuth()
  const [rows, setRows] = useState<CountryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchAllCountries(ac.signal)
      .then((r) => {
        if (alive) setRows(r)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const kpi = useMemo(() => {
    const blocked = rows.filter((r) => r.is_blocked).length
    const users = rows.reduce((s, r) => s + (r.users || 0), 0)
    return { total: rows.length, blocked, open: rows.length - blocked, users }
  }, [rows])

  if (!can(PERMISSIONS.COUNTRIES_VIEW)) return <PermissionDenied permission={PERMISSIONS.COUNTRIES_VIEW} />

  const cards = [
    { label: S.totalCountries, value: kpi.total },
    { label: S.openCountries, value: kpi.open },
    { label: S.blockedCountries, value: kpi.blocked },
    { label: S.totalUsers, value: kpi.users },
  ]

  return (
    <>
      <PageHeader
        title={S.mapTitle}
        screenId="A11.01"
        description={S.mapNote}
        actions={
          <Link
            to="/geo"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {S.backToList}
          </Link>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="space-y-3">
            <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-16" />
              ))}
            </div>
            <Skeleton className="h-64 w-full" />
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !rows.length ? (
          <EmptyState icon={<Globe size={28} strokeWidth={1.5} />} />
        ) : (
          <div className="space-y-4">
            <div className="grid max-w-3xl grid-cols-2 gap-2 md:grid-cols-4">
              {cards.map((c) => (
                <div key={c.label} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <p className="text-xs text-tertiary">{c.label}</p>
                  <p className="mt-1 font-mono text-xl tabular text-primary">{c.value.toLocaleString()}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {rows.map((c) => (
                <Link
                  key={c.code}
                  to={`/geo/${c.code}`}
                  title={`${c.name} · ${c.users.toLocaleString()}`}
                  className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm hover:border-border-strong ${
                    c.is_blocked
                      ? 'border-danger/40 bg-danger/10 text-danger'
                      : 'border-success/40 bg-success/10 text-primary'
                  }`}
                >
                  <span>{c.flag || '🌐'}</span>
                  <span className="font-mono text-xs">{c.code}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
